import { MessageOutlined, PlusOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, Modal, Select } from 'antd';
import * as api from '../lib/api';
import type { Channel } from '../types';
import { STATUS_COLUMNS, STATUS_CONFIG } from '../lib/taskStatus';
import { useTaskStore } from '../stores/useTaskStore';
import TaskRowActions from './TaskRowActions';
import ParticipantAvatars from './ParticipantAvatars';
import surface from './ProjectSurface.module.css';
import styles from './ServerTasksPage.module.css';
import ProjectPageHeader from './ProjectPageHeader';

export default function ServerTasksPage({
  guildId,
  activeThreadId,
  onTaskClick,
}: {
  guildId: string;
  activeThreadId?: string;
  onTaskClick: (task: api.Task) => void;
}) {
  const byTaskId = useTaskStore((state) => state.byTaskId);
  const requestedChannelId = typeof window === 'undefined'
    ? undefined
    : new URLSearchParams(window.location.search).get('channel') ?? undefined;
  const [channels, setChannels] = useState<Channel[]>([]);
  const [members, setMembers] = useState<api.ChannelMember[]>([]);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<string>();
  const [channel, setChannel] = useState<string | undefined>(requestedChannelId);
  const [assignee, setAssignee] = useState<string>();
  const [creating, setCreating] = useState(false);
  const load = useCallback(async () => {
    setReady(false);
    setLoadError(null);
    try {
      const [result, roster] = await Promise.all([
        api.fetchGuildChannels(guildId),
        api.fetchGuildMembers(guildId),
        useTaskStore.getState().loadGuildTasks(guildId),
      ]);
      setChannels(result.channels);
      setMembers(roster);
    } catch {
      setChannels([]);
      setMembers([]);
      useTaskStore.setState((state) => ({
        byTaskId: Object.fromEntries(
          Object.entries(state.byTaskId).filter(([, task]) => task.guild_id !== guildId),
        ),
      }));
      setLoadError('Could not load Project tasks.');
    } finally {
      setReady(true);
    }
  }, [guildId]);
  useEffect(() => {
    void load();
  }, [load]);
  const tasks = useMemo(
    () =>
      Object.values(byTaskId)
        .filter((task) => {
          if (task.guild_id !== guildId) return false;
          const haystack =
            `${task.title} ${task.description} ${task.channel?.channel_name ?? ''}`.toLowerCase();
          return (
            (!query || haystack.includes(query.toLowerCase())) &&
            (!status || task.status === status) &&
            (!channel || task.channel_id === channel) &&
            (!assignee || task.assignee_id === assignee)
          );
        })
        .sort((a, b) => b.updated_at - a.updated_at),
    [assignee, byTaskId, channel, guildId, query, status],
  );
  const update = (updated: api.Task) =>
    useTaskStore.setState((state) => ({ byTaskId: { ...state.byTaskId, [updated.id]: updated } }));
  const remove = (taskId: string) =>
    useTaskStore.setState((state) => {
      const next = { ...state.byTaskId };
      delete next[taskId];
      return { byTaskId: next };
    });
  if (!ready) return <div className={surface.loading}>Loading Project tasks…</div>;
  if (loadError)
    return (
      <main className={surface.pageRoot}>
        <section className={`${surface.pageContainer} ${surface.section}`}>
          <div className={surface.listEmpty} role="alert">
            {loadError}
            <button type="button" className="btn btn-sm" onClick={() => void load()}>
              Retry
            </button>
          </div>
        </section>
      </main>
    );
  return (
    <main className={surface.pageRoot}>
      <ProjectPageHeader
        title="Tasks"
        end={<Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>New Task</Button>}
      />
      <div className={`${surface.pageContainer} ${styles.toolbar}`}>
        <Input
          className={styles.search}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search tasks…"
          aria-label="Search Project tasks"
        />
        <Select
          allowClear
          value={status}
          onChange={setStatus}
          placeholder="Status"
          options={STATUS_COLUMNS.map(({ key, label }) => ({ value: key, label }))}
        />
        <Select
          allowClear
          value={channel}
          onChange={setChannel}
          placeholder="Channel"
          options={channels.map((item) => ({ value: item.id, label: `# ${item.name}` }))}
        />
        <Select
          allowClear
          value={assignee}
          onChange={setAssignee}
          placeholder="Assignee"
          options={members.map((item) => ({ value: item.user_id, label: item.display_name }))}
        />
      </div>
      <section className={`${surface.pageContainer} ${surface.section}`}>
        <h2 className={surface.sectionTitle}>
          {query ? 'Search results' : 'All tasks'}
          {tasks.length ? ` · ${tasks.length}` : ''}
        </h2>
        <div className={surface.borderedList}>
          {tasks.length ? (
            tasks.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                active={task.thread_id === activeThreadId}
                onClick={onTaskClick}
                onUpdated={update}
                onDeleted={remove}
                channels={channels}
                members={members}
              />
            ))
          ) : (
            <div className={surface.listEmpty}>No matching tasks.</div>
          )}
        </div>
      </section>
      {creating && (
        <CreateServerTask
          channels={channels}
          members={members}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void load();
          }}
        />
      )}
    </main>
  );
}
function TaskRow({
  task,
  active,
  onClick,
  onUpdated,
  onDeleted,
  channels,
  members,
}: {
  task: api.Task;
  active: boolean;
  onClick: (task: api.Task) => void;
  onUpdated: (task: api.Task) => void;
  onDeleted: (id: string) => void;
  channels: Channel[];
  members: api.ChannelMember[];
}) {
  const cfg = STATUS_CONFIG[task.status];
  const channelName =
    task.channel?.channel_name ?? channels.find((item) => item.id === task.channel_id)?.name;
  const participants = (task.participants ?? [])
    .map((id) => members.find((member) => member.user_id === id))
    .filter((member): member is api.ChannelMember => !!member)
    .map((member) => ({
      user_id: member.user_id,
      display_name: member.display_name,
      avatar_url: member.avatar_url ?? undefined,
    }));
  return (
    <div className={`${surface.listRow} ${styles.taskRow} ${active ? surface.listRowActive : ''}`}>
      <button
        type="button"
        className={styles.rowOpener}
        onClick={() => task.thread_id && onClick(task)}
        disabled={!task.thread_id}
        aria-label={task.thread_id ? `Open ${task.title}` : `${task.title} has no task thread`}
      >
        <span className={styles.status} style={{ background: cfg?.color ?? '#a1a1aa' }} />
        <span className={styles.copy}>
          <strong>{task.title}</strong>
          <small>{channelName ? `#${channelName}` : 'Channel unavailable'}</small>
        </span>
        <span className={styles.meta}>{cfg?.label ?? task.status}</span>
        {participants.length > 0 && <ParticipantAvatars participants={participants} size={18} />}
        {task.thread_id && <MessageOutlined className={styles.thread} />}
      </button>
      <TaskRowActions task={task} onUpdated={onUpdated} onDeleted={onDeleted} />
    </div>
  );
}
function CreateServerTask({
  channels,
  members,
  onClose,
  onCreated,
}: {
  channels: Channel[];
  members: api.ChannelMember[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [channelId, setChannelId] = useState<string>();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assigneeId, setAssigneeId] = useState<string>();
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!channelId || !title.trim() || saving) return;
    setSaving(true);
    try {
      await api.createTask(channelId, {
        title: title.trim(),
        description: description.trim(),
        assignee_id: assigneeId,
      });
      onCreated();
    } catch {
      setSaving(false);
    }
  };
  return (
    <Modal
      title="New Task"
      open
      onCancel={onClose}
      onOk={save}
      okText={saving ? 'Creating…' : 'Create'}
      okButtonProps={{ disabled: !channelId || !title.trim() || saving }}
    >
      <label className={styles.modalField}>
        Channel
        <Select
          value={channelId}
          onChange={setChannelId}
          placeholder="Select a channel"
          options={channels.map((item) => ({ value: item.id, label: `# ${item.name}` }))}
        />
      </label>
      <label className={styles.modalField}>
        Title
        <Input autoFocus value={title} onChange={(event) => setTitle(event.target.value)} />
      </label>
      <label className={styles.modalField}>
        Description
        <Input.TextArea
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          rows={3}
        />
      </label>
      <label className={styles.modalField}>
        Assignee
        <Select
          allowClear
          value={assigneeId}
          onChange={setAssigneeId}
          options={members.map((item) => ({ value: item.user_id, label: item.display_name }))}
        />
      </label>
    </Modal>
  );
}
